<template>
  <Layout>
    <PageHeader :title="listName" :items="breadcrumb" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-2">
              <div class="col-sm-4">
                <div class="search-box mr-2 mb-2 d-inline-block">
                  <div class="position-relative">
                    <input type="text" class="form-control" v-model="search" placeholder="Buscar por nome..." @keyup.enter="doSearch"/>
                    <i class="bx bx-search-alt search-icon"></i>
                  </div>
                </div>
              </div>
              <div class="col-sm-8">
                <div class="float-sm-end d-flex">
                  <div class="text-sm-right">
                    <button type="button" class="btn btn-info btn-rounded mb-2 mr-2" @click="openImportModal()">
                      <i class="bx bx-import mr-1"></i> Importar
                    </button>
                    <button type="button" class="btn btn-success btn-rounded mb-2 mr-2" @click="openItemModal()">
                      <i class="mdi mdi-plus mr-1"></i> Novo Item
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="table-responsive">
              <table class="table table-centered table-nowrap table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nome</th>
                    <th v-for="col in listColumns" :key="col.key">{{ col.label }}</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in items" :key="item.id">
                    <td>{{ item.id }}</td>
                    <td>{{ item.name }}</td>
                    <td v-for="col in listColumns" :key="col.key">
                      {{ item.data ? item.data[col.key] : '' }}
                    </td>
                    <td>
                      <b-dropdown class="card-drop" variant="white" right toggle-class="p-0">
                        <template v-slot:button-content>
                          <i class="mdi mdi-dots-horizontal font-size-18"></i>
                        </template>
                        <b-dropdown-item @click="openItemModal(item)">
                          <i class="fas fa-pencil-alt text-success mr-1"></i> Editar
                        </b-dropdown-item>
                        <b-dropdown-item @click="deleteItem(item.id)">
                          <i class="fas fa-trash-alt text-danger mr-1"></i> Excluir
                        </b-dropdown-item>
                      </b-dropdown>
                    </td>
                  </tr>
                  <tr v-if="!items.length">
                    <td :colspan="3 + listColumns.length" class="text-center text-muted py-4">
                      Nenhum item encontrado.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" @input="updateItems"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Criar/Editar Item -->
    <b-modal v-model="itemModal" :title="itemModalTitle" size="md" :no-enforce-focus="true" :no-close-on-backdrop="true">
      <div class="form-group">
        <label for="itemName">Nome</label>
        <input
          id="itemName"
          v-model="itemForm.name"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': itemSubmitted && !itemForm.name }"
        />
        <div v-if="itemSubmitted && !itemForm.name" class="invalid-feedback">
          O campo nome é obrigatório.
        </div>
      </div>
      <div v-for="col in listColumns" :key="col.key" class="form-group mt-3">
        <label :for="'item-' + col.key">{{ col.label }}</label>
        <input
          :id="'item-' + col.key"
          v-model="itemForm.data[col.key]"
          type="text"
          class="form-control"
        />
      </div>
      <template v-slot:modal-footer>
        <b-button variant="secondary" @click="closeItemModal()">Cancelar</b-button>
        <button type="button" class="btn btn-primary" @click="saveItemAction()">Salvar</button>
      </template>
    </b-modal>

    <!-- Modal Importar -->
    <b-modal v-model="importModal" title="Importar Items" size="md" :no-enforce-focus="true" :no-close-on-backdrop="true">
      <div class="mb-3">
        <p class="text-muted">
          O arquivo deve conter as colunas: <strong>nome</strong> (obrigatório)
          <span v-for="col in listColumns" :key="col.key">, <strong>{{ col.key }}</strong></span>.
        </p>
      </div>
      <div class="form-group">
        <label>Arquivo (XLS, XLSX ou CSV)</label>
        <b-form-file
          v-model="importFile"
          browse-text="Procurar"
          accept=".xls,.xlsx,.csv"
          placeholder="Escolha um arquivo para importar"
          drop-placeholder="Jogue arquivo aqui..."
        ></b-form-file>
      </div>
      <template v-slot:modal-footer>
        <b-button variant="secondary" @click="importModal = false">Cancelar</b-button>
        <button type="button" class="btn btn-primary" :disabled="!importFile" @click="doImport()">Importar</button>
      </template>
    </b-modal>
  </Layout>
</template>
<script>
import Swal from 'sweetalert2'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapActions, mapGetters, mapState } from 'vuex'
import permissionMixin from '@/mixins/permissionMixin'

export default {
  mixins: [permissionMixin],
  components: {
    Layout,
    PageHeader
  },
  name: 'CustomListShow',
  page: {
    title: 'Items da Lista',
    meta: [{
      name: 'description',
      content: appConfig.description
    }]
  },
  data () {
    return {
      loading: true,
      search: '',
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      // Item modal
      itemModal: false,
      itemModalTitle: 'Novo Item',
      itemSubmitted: false,
      itemForm: { name: '', data: {} },
      // Import modal
      importModal: false,
      importFile: null,
      breadcrumb: [
        { text: 'Listas Customizadas', href: '/custom-lists' },
        { text: 'Items', active: true }
      ]
    }
  },
  mounted () {
    this.loadList()
    this.updateItems()
  },
  computed: {
    ...mapGetters('customList', { list: 'CUSTOM_LIST', items: 'CUSTOM_LIST_ITEMS' }),
    ...mapState('customList', ['itemsPagination']),
    listId () {
      return this.$route.params.id
    },
    listName () {
      return this.list.name || 'Items da Lista'
    },
    listColumns () {
      return this.list.columns || []
    }
  },
  methods: {
    ...mapActions('customList', {
      showList: 'show',
      getItems: 'getItems',
      saveItem: 'saveItem',
      destroyItemAction: 'destroyItem',
      importItems: 'importItems'
    }),
    loadList () {
      this.showList(this.listId)
    },
    openItemModal (item = null) {
      this.itemSubmitted = false
      // Build data object with all column keys initialized
      let data = {}
      this.listColumns.forEach(col => { data[col.key] = '' })

      if (item) {
        this.itemModalTitle = 'Editar Item'
        // Merge existing data (ensure it's an object, not array)
        const existingData = (item.data && !Array.isArray(item.data)) ? item.data : {}
        Object.assign(data, existingData)
        this.itemForm = {
          id: item.id,
          name: item.name,
          data: data
        }
      } else {
        this.itemModalTitle = 'Novo Item'
        this.itemForm = { name: '', data: data }
      }
      this.itemModal = true
    },
    closeItemModal () {
      this.itemModal = false
      this.itemForm = { name: '', data: {} }
      this.itemSubmitted = false
    },
    async saveItemAction () {
      this.itemSubmitted = true
      if (!this.itemForm.name) return

      try {
        await this.saveItem({ listId: this.listId, item: this.itemForm })
        successMessage('Sucesso: Item salvo com sucesso.', 2500)
        this.closeItemModal()
        this.updateItems()
      } catch (err) {
        errorMessage('Alerta: ' + (err.response ? err.response.data.message : err.message))
      }
    },
    deleteItem (itemId) {
      Swal.fire({
        title: 'Você tem certeza ?',
        text: 'Essa ação não poderá ser revertida',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        cancelButtonColor: '#4a4848',
        cancelButtonText: 'CANCELAR',
        confirmButtonText: 'SIM, DESEJO EXCLUIR!'
      }).then(result => {
        if (result.value) {
          try {
            this.destroyItemAction({ listId: this.listId, itemId: itemId })
            successMessage('Sucesso: Item excluído com sucesso.', 2500)
            this.updateItems()
          } catch (err) {
            errorMessage('Alerta: ' + (err.response ? err.response.data.message : err.message))
          }
        }
      })
    },
    openImportModal () {
      this.importFile = null
      this.importModal = true
    },
    async doImport () {
      if (!this.importFile) return

      let loader = this.$loading.show()
      try {
        const formData = new FormData()
        formData.append('file', this.importFile)
        await this.importItems({ listId: this.listId, formData: formData })
        successMessage('Sucesso: Importação iniciada. Os items serão processados em breve.', 3500)
        this.importModal = false
        loader.hide()
        // Aguarda um pouco e atualiza a lista
        setTimeout(() => { this.updateItems() }, 2000)
      } catch (err) {
        loader.hide()
        errorMessage('Alerta: ' + (err.response ? err.response.data.message : err.message))
      }
    },
    doSearch () {
      this.currentPage = 1
      this.updateItems()
    },
    updateItems () {
      let params = {
        search: this.search || null,
        per_page: this.perPage,
        page: this.currentPage
      }
      let loader = this.$loading.show()
      this.getItems({ listId: this.listId, params: params }).then(() => {
        this.totalRows = this.itemsPagination.total
        this.currentPage = this.itemsPagination.current_page
        loader.hide()
      }).catch(error => {
        console.log(error)
        loader.hide()
      })
    }
  }
}
</script>
<style scoped>
.float-sm-end { float: right }
.table-responsive { min-height: 300px; }
.mr-2 { margin-right: 0.5rem; }
</style>
